/* eslint-disable no-param-reassign */
import { createSlice, isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { notification } from 'antd';
import {
    getProjectCategories,
    getSchoolProjects,
    createOrUpdateProject,
    exportProjects,
    deleteProject,
} from './projectsApi';
import {
    logoutUser,
} from '../user/userApi';
import { ProjectsState } from './types';

const initialState: ProjectsState = {
    error: null,
    isFetching: false,
    categories: [],
    isCategoriesFetching: false,
    projects: [],
    singleProject: null,
    isExporting: false,
};

export const projectsSlice = createSlice({
    name: 'projects',
    initialState,
    reducers: {
        loadSingleProject: (state, action: PayloadAction<number>) => {
            const project = state.projects.find(p => p.id === action.payload) || null;
            state.singleProject = project;
        },
        resetSingleProject: (state) => {
            state.singleProject = null;
        },
    },
    extraReducers: (builder) => {
        // Get project categories
        builder
            .addCase(getProjectCategories.fulfilled, (state: ProjectsState, action) => {
                state.isCategoriesFetching = false;
                state.categories = action.payload?.categories || [];
            });

        // Get school projects
        builder
            .addCase(getSchoolProjects.fulfilled, (state: ProjectsState, action) => {
                state.isFetching = false;
                state.projects = action.payload?.projects || [];
            });

        // Create or update project
        builder
            .addCase(createOrUpdateProject.fulfilled, (state: ProjectsState, action) => {
                state.isFetching = false;
                const updatedProject = action.payload?.project;

                if (updatedProject) {
                    // If updating, find and replace; if creating, add to array
                    const index = state.projects.findIndex(p => p.id === updatedProject.id);
                    if (index !== -1) {
                        state.projects[index] = updatedProject;
                    } else {
                        state.projects.push(updatedProject);
                    }
                    state.singleProject = null;
                }

                notification.success({
                    message: 'Pavyko!',
                    description: action.payload?.message || 'Projektas sėkmingai išsaugotas',
                });
            });

        // Export projects
        builder
            .addCase(exportProjects.fulfilled, (state: ProjectsState) => {
                state.isExporting = false;
            });

        // Delete project
        builder
            .addCase(deleteProject.fulfilled, (state: ProjectsState, action) => {
                state.isFetching = false;
                const projectId = action.meta.arg.id;
                state.projects = state.projects.filter(p => p.id !== projectId);
                notification.success({
                    message: 'Pavyko!',
                    description: action.payload?.message || 'Projektas sėkmingai ištrintas',
                });
            });

        // On logout
        builder
            .addCase(logoutUser.fulfilled, (state: ProjectsState) => {
                state.projects = [];
                state.singleProject = null;
                state.categories = [];
            });

        // Pending states
        builder.addMatcher(
            isAnyOf(
                getProjectCategories.pending,
            ),
            (state: ProjectsState) => {
                state.isCategoriesFetching = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                getSchoolProjects.pending,
                createOrUpdateProject.pending,
                deleteProject.pending,
            ),
            (state: ProjectsState) => {
                state.isFetching = true;
                state.error = null;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportProjects.pending,
            ),
            (state: ProjectsState) => {
                state.isExporting = true;
                state.error = null;
            },
        );

        // Error states
        builder.addMatcher(
            isAnyOf(
                getProjectCategories.rejected,
                getSchoolProjects.rejected,
                createOrUpdateProject.rejected,
                deleteProject.rejected,
            ),
            (state: ProjectsState, action) => {
                state.isFetching = false;
                state.isCategoriesFetching = false;
                state.error = action.error;
            },
        );

        builder.addMatcher(
            isAnyOf(
                exportProjects.rejected,
            ),
            (state: ProjectsState, action) => {
                state.isExporting = false;
                state.error = action.error;
            },
        );
    },
});

export const { loadSingleProject, resetSingleProject } = projectsSlice.actions;
export default projectsSlice.reducer;
